import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { EInboxType } from '../../store/models/user.model';
import { MatRadioChange } from '@angular/material/radio';
import { ContactsService } from '../../store/services/contacts.service';
import { ICampaign } from '../../store/models/campaigns.model';
import { ISelectItem } from '../../store/models/select.model';
import { IContactGroup } from '../../store/models/contact.model';
import { Button, ButtonColor, ButtonSize } from '../../store/models/button.model';
import { SessionService } from '../../store/services/session.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AccountService } from '../../store/services/account.service';
import { Store } from '@ngrx/store';
import { IReducers } from '../../store/reducers';
import { openSnackbar } from '../../store/actions/page.action';
import { getUserSession } from '../../store/actions/user.action';

@Component({
  selector: 'app-inbox-management',
  templateUrl: './inbox-management.component.html',
  styleUrls: ['./inbox-management.component.scss'],
})
export class InboxManagementComponent implements OnInit, OnDestroy {
  turnOffButtonProperties: Button;

  turnOnButtonProperties: Button = {
    text: 'Turn On',
    color: ButtonColor.GREEN,
    size: ButtonSize.NORMAL,
    onClick: () => this.turnOn(),
  };

  formGroup: FormGroup;

  enableInboxCleaningPanel: boolean;
  showInboxCapaignConfig: boolean;

  enableMeetingPanel: boolean;

  publishedCampaigns: ICampaign[] = [];
  inboxSelectedCampaignId: number;
  inboxCampaignContactGroups: IContactGroup[] = [];

  meetingPublishedCampaigns: ICampaign[] = [];
  meetingSelectedCampaignId: number;
  meetingCampaignContactGroups: IContactGroup[] = [];

  accountId: number;

  componentReady: boolean;

  private destory$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private readonly contactsService: ContactsService,
    private readonly sessionService: SessionService,
    private readonly accountService: AccountService,
    private readonly store$: Store<IReducers>,
  ) {
  }

  ngOnInit(): void {
    this.sessionService
      .onReady()
      .pipe(takeUntil(this.destory$))
      .subscribe(session => {
        if (!session) {
          return;
        }

        this.accountId = session.id;

        const currentInboxManagement = session.inbox_management_settings;

        if (currentInboxManagement.on) {
          this.turnOnButtonProperties.text = 'Update';
          this.turnOffButtonProperties = {
            text: 'Turn Off',
            color: ButtonColor.ORANGE,
            size: ButtonSize.NORMAL,
            onClick: () => this.turnOff(),
          };
        } else {
          this.turnOffButtonProperties = undefined;
          this.turnOnButtonProperties.text = 'Turn On and Save';
        }

        this.contactsService.getPublishedCampaigns().subscribe(campaigns => {
          if (campaigns && campaigns.items && campaigns.items.length) {
            this.publishedCampaigns = campaigns.items;
          }

          if (this.formGroup !== undefined) {
            this.componentReady = true;
            return;
          }

          this.formGroup = new FormGroup({
            enabled: new FormControl(currentInboxManagement.enabled, [
              Validators.required,
            ]),
            inbox_delay: new FormControl(
              currentInboxManagement.inbox_delay || 15,
              [Validators.min(15), Validators.required],
            ),
            inbox_type: new FormControl(
              currentInboxManagement.inbox_type || EInboxType.CYCLE,
              [Validators.required],
            ),
            inbox_campaign: new FormControl(
              currentInboxManagement.inbox_campaign || '',
              currentInboxManagement.inbox_type ===
              EInboxType.CAMPAIGN.toString()
                ? [Validators.required]
                : [],
            ),
            inbox_group: new FormControl(
              currentInboxManagement.inbox_group || '',
              currentInboxManagement.inbox_type ===
              EInboxType.CAMPAIGN.toString()
                ? [Validators.required]
                : [],
            ),
            meeting_delay: new FormControl(
              currentInboxManagement.meeting_delay || 15,
              [Validators.min(15), Validators.required],
            ),
            meeting_campaign: new FormControl(
              currentInboxManagement.meeting_campaign || '',
              [Validators.required],
            ),
            meeting_group: new FormControl(
              currentInboxManagement.meeting_group || '',
              [Validators.required],
            ),
            on: new FormControl(currentInboxManagement.on, [
              Validators.required,
            ]),
          });

          this.componentReady = true;

          if (
            (currentInboxManagement.inbox_campaign &&
              currentInboxManagement.inbox_group) ||
            currentInboxManagement.inbox_type === EInboxType.CYCLE
          ) {
            this.enableInboxCleaningPanel = true;
          }

          if (
            currentInboxManagement.meeting_campaign &&
            currentInboxManagement.meeting_group
          ) {
            this.enableMeetingPanel = true;
          }

          if (currentInboxManagement.inbox_type === EInboxType.CAMPAIGN) {
            this.showInboxCapaignConfig = true;
          }
        });
      });
  }

  toggleCleaningPanel() {
    this.enableInboxCleaningPanel = !this.enableInboxCleaningPanel;

    if (!this.enableInboxCleaningPanel) {
      this.formGroup.get('inbox_delay').removeValidators([Validators.required]);
      this.formGroup.get('inbox_delay').setValue(15);
      this.formGroup.get('inbox_type').removeValidators([Validators.required]);
      this.formGroup.get('inbox_type').setValue(EInboxType.CYCLE);
      this.formGroup
        .get('inbox_campaign')
        .removeValidators([Validators.required]);
      this.formGroup.get('inbox_campaign').setValue('');
      this.formGroup.get('inbox_group').removeValidators([Validators.required]);
      this.formGroup.get('inbox_group').setValue('');
      this.formGroup.updateValueAndValidity();
    } else {
      this.formGroup
        .get('inbox_delay')
        .setValidators([Validators.required, Validators.min(15)]);
      this.formGroup.get('inbox_type').setValidators([Validators.required]);
      this.formGroup.get('inbox_campaign').setValidators([Validators.required]);
      this.formGroup.get('inbox_group').setValidators([Validators.required]);
      this.formGroup.updateValueAndValidity();
    }

    if (this.formGroup.get('inbox_type').value === 'cycle') {
      this.formGroup
        .get('inbox_campaign')
        .removeValidators([Validators.required]);
      this.formGroup.get('inbox_campaign').setValue('');
      this.formGroup.get('inbox_group').removeValidators([Validators.required]);
      this.formGroup.get('inbox_group').setValue('');
    }
  }

  toggleMeetingPanel(updatePanel = true) {
    if (updatePanel) {
      this.enableMeetingPanel = !this.enableMeetingPanel;
    }

    if (!this.enableMeetingPanel) {
      this.formGroup
        .get('meeting_delay')
        .setValidators(null);
      this.formGroup.get('meeting_delay').setValue(15);
      this.formGroup
        .get('meeting_campaign')
        .setValidators(null);
      this.formGroup.get('meeting_campaign').setValue('');
      this.formGroup
        .get('meeting_group')
        .setValidators(null);
      this.formGroup.get('meeting_group').setValue('');
      this.formGroup.updateValueAndValidity();
    } else {
      this.formGroup
        .get('meeting_delay')
        .setValidators([Validators.required, Validators.min(15)]);
      this.formGroup
        .get('meeting_campaign')
        .setValidators([Validators.required]);
      this.formGroup.get('meeting_campaign').markAsUntouched();
      this.formGroup.get('meeting_campaign').updateValueAndValidity();
      this.formGroup.get('meeting_group').setValidators([Validators.required]);
      this.formGroup.get('meeting_group').markAsUntouched();
      this.formGroup.get('meeting_group').updateValueAndValidity();
      this.formGroup.updateValueAndValidity();
    }
  }

  onInboxTypeChange($event: MatRadioChange, isMeeting: boolean = false) {
    this.showInboxCapaignConfig =
      $event.value === EInboxType.CAMPAIGN.toString();

    if ($event.value === EInboxType.CYCLE.toString()) {
      this.formGroup
        .get('inbox_campaign')
        .removeValidators([Validators.required]);
      this.formGroup.get('inbox_campaign').setValue('');
      this.formGroup.get('inbox_group').removeValidators([Validators.required]);
      this.formGroup.get('inbox_group').setValue('');
      this.formGroup.updateValueAndValidity();
      this.inboxSelectedCampaignId = null;
    }
  }

  getCampaignsSelectItems(): ISelectItem[] {
    return this.publishedCampaigns.map(c => ({
      label: c.campaign_name,
      value: c.campaign_id,
    }));
  }

  onCampaignSelection($event: number, isMeeting: boolean = false) {
    if (!$event) {
      return;
    }
    if (isMeeting) {
      this.meetingSelectedCampaignId = $event;
    } else {
      this.inboxSelectedCampaignId = $event;
    }
    this.contactsService
      .getContactGroupsByCampaignId($event)
      .subscribe(contacts => {
        if (isMeeting) {
          this.meetingCampaignContactGroups = contacts.items;
          return;
        }
        this.inboxCampaignContactGroups = contacts.items;
      });
  }

  getContactGroupsSelectItems(isMeeting: boolean = false) {
    if (isMeeting) {
      return this.meetingCampaignContactGroups.map(c => ({
        label: c.group_name,
        value: c.group_id,
      }));
    }
    return this.inboxCampaignContactGroups.map(c => ({
      label: c.group_name,
      value: c.group_id,
    }));
  }

  normalizeFormValues() {
    const formValue = this.formGroup.value;
    if (!formValue.inbox_delay) {
      formValue.inbox_delay = null;
    }

    if (!formValue.meeting_delay) {
      formValue.meeting_delay = null;
    }

    if (!formValue.inbox_campaign) {
      formValue.inbox_campaign = null;
    }
    if (!formValue.inbox_group) {
      formValue.inbox_group = null;
    }

    if (!this.enableInboxCleaningPanel) {
      formValue.inbox_campaign = null;
      formValue.inbox_group = null;
      formValue.inbox_delay = null;
      formValue.inbox_type = null;
    }

    if (!formValue.meeting_campaign) {
      formValue.meeting_campaign = null;
    }

    if (!formValue.meeting_group) {
      formValue.meeting_group = null;
    }

    if (!this.enableMeetingPanel) {
      formValue.meeting_delay = null;
      formValue.meeting_campaign = null;
      formValue.meeting_group = null;
    }

    return formValue;
  }

  ngOnDestroy(): void {
    this.destory$.next(true);
    this.destory$.complete();
  }

  private turnOff() {
    this.componentReady = false;
    this.formGroup.get('on').setValue(false);
    this.formGroup.get('inbox_delay').setValue('');
    this.formGroup.get('inbox_campaign').setValue('');
    this.formGroup.get('inbox_group').setValue('');
    this.formGroup.get('meeting_delay').setValue('');
    this.formGroup.get('meeting_campaign').setValue('');
    this.formGroup.get('meeting_group').setValue('');
    this.showInboxCapaignConfig = false;
    this.enableMeetingPanel = false;
    this.enableInboxCleaningPanel = false;
    this.inboxCampaignContactGroups = [];
    this.meetingCampaignContactGroups = [];
    this.inboxSelectedCampaignId = null;
    this.meetingSelectedCampaignId = null;

    this.accountService
      .updateInboxManagementSettings(this.accountId, this.normalizeFormValues())
      .subscribe(
        () => {
          this.store$.dispatch(getUserSession());
          this.store$.dispatch(
            openSnackbar({
              snackbarData: {
                data: {
                  message: 'Inbox management has been turned off',
                  type: 'success',
                },
              },
            }),
          );
        },
        error => {
          this.store$.dispatch(
            openSnackbar({
              snackbarData: {
                data: {
                  message: error.error,
                  type: 'error',
                },
              },
            }),
          );
          this.componentReady = true;
        },
      );
  }

  private turnOn() {
    this.toggleMeetingPanel(false);

    if (!this.formGroup.valid) {
      this.formGroup.markAllAsTouched();
      return;
    }
    this.componentReady = false;
    this.meetingSelectedCampaignId = null;
    this.formGroup.get('on').setValue(true);

    this.accountService
      .updateInboxManagementSettings(this.accountId, this.normalizeFormValues())
      .subscribe(
        () => {
          this.store$.dispatch(getUserSession());
          this.store$.dispatch(
            openSnackbar({
              snackbarData: {
                data: {
                  message: 'Inbox management settings have been updated',
                  type: 'success',
                },
              },
            }),
          );

          this.componentReady = true;
        },
        error => {
          this.store$.dispatch(
            openSnackbar({
              snackbarData: {
                data: {
                  message: error.error,
                  type: 'error',
                },
              },
            }),
          );

          this.componentReady = true;
        },
      );
  }
}
